'use client'

import * as React from 'react'
import Image, { ImageProps } from 'next/image'
import { cn } from '@/utils/styles/cn'
import { createImageUrl } from '@/utils/image/create-image-url'

const AvatarContext = React.createContext<{
    isImageLoaded: boolean
    hasImageError: boolean
    imagePresent: boolean
}>({
    isImageLoaded: false,
    hasImageError: false,
    imagePresent: false,
})

type AvatarProps = React.HTMLAttributes<HTMLDivElement> & {
    className?: string
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(({ className, ...props }, ref) => {
    const [isImageLoaded, setIsImageLoaded] = React.useState(false)
    const [hasImageError, setHasImageError] = React.useState(false)
    const [imagePresent, setImagePresent] = React.useState(false)

    return (
        <AvatarContext.Provider value={{ isImageLoaded, hasImageError, imagePresent }}>
            <div
                ref={ref}
                className={cn('relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full', className)}
                {...props}
                data-state={isImageLoaded ? 'loaded' : 'loading'}
            >
                <AvatarContext.Provider
                    value={
                        {
                            isImageLoaded,
                            hasImageError,
                            imagePresent,
                            setIsImageLoaded: (value: boolean) => setIsImageLoaded(value),
                            setHasImageError: (value: boolean) => setHasImageError(value),
                            setImagePresent: (value: boolean) => setImagePresent(value),
                        } as any
                    }
                >
                    {props.children}
                </AvatarContext.Provider>
            </div>
        </AvatarContext.Provider>
    )
})
Avatar.displayName = 'Avatar'

type AvatarImageProps = Omit<ImageProps, 'src'> & {
    src: string | null | undefined
}

const AvatarImage = React.forwardRef<HTMLImageElement, AvatarImageProps>(({ className, src, alt, ...props }, ref) => {
    const context = React.useContext(AvatarContext) as any

    React.useEffect(() => {
        if (src && !context.imagePresent) {
            context.setImagePresent(true)
        }
    }, [src, context])

    if (!src) {
        return null
    }

    return (
        <div className="absolute inset-0 h-full w-full">
            <Image
                ref={ref}
                src={createImageUrl(src, {
                    width: 40,
                    height: 40,
                })}
                alt={alt || 'Avatar'}
                fill
                sizes="100%"
                className={cn('h-full w-full object-cover', className)}
                onLoadingComplete={() => context.setIsImageLoaded(true)}
                onError={() => context.setHasImageError(true)}
                {...props}
            />
        </div>
    )
})
AvatarImage.displayName = 'AvatarImage'

type AvatarFallbackProps = React.HTMLAttributes<HTMLDivElement> & {
    delayMs?: number
}

const AvatarFallback = React.forwardRef<HTMLDivElement, AvatarFallbackProps>(
    ({ className, delayMs = 0, ...props }, ref) => {
        const [isMounted, setIsMounted] = React.useState(false)
        const { isImageLoaded, hasImageError, imagePresent } = React.useContext(AvatarContext)

        React.useEffect(() => {
            const timer = setTimeout(() => setIsMounted(true), delayMs)
            return () => clearTimeout(timer)
        }, [delayMs])

        if (!isMounted) return null

        // Only show fallback if:
        // 1. No image is provided, or
        // 2. Image has error, or
        // 3. Image is present but not loaded yet
        const showFallback = !imagePresent || hasImageError || (imagePresent && !isImageLoaded)

        if (!showFallback) return null

        return (
            <div
                ref={ref}
                className={cn('flex h-full w-full items-center justify-center rounded-full bg-muted', className)}
                {...props}
            />
        )
    }
)
AvatarFallback.displayName = 'AvatarFallback'

export { Avatar, AvatarImage, AvatarFallback }
